import { defineStore } from 'pinia'
import { useForm } from '@/composables/useForm.ts'
import type { Consultation, ConsultationSummary, Pagination } from '@/types'
import { CONSULTATION_MEDIUMS, CONSULTATION_STATUSES } from '@/enum/consultation.ts'
import Pusher, { Channel } from 'pusher-js'
import { useEventBus } from '@vueuse/core'
import type { GetConsultationForm } from '@/types/forms/Consultation.ts'
import { consultationStatus } from '@/enum/consultations.ts'
import type { ValueOf } from 'type-fest'
import { isAxiosError } from 'axios'
import type { Recommendation } from '@/types/Recommendation.ts'
import { differenceInDaysFromNow } from '@/utils/date.ts'

export const useConsultationsStore = defineStore('consultations', () => {
  const authStore = useAuthStore()
  const currentConsultation = ref<Consultation | null | undefined>(null)
  const pendingRecommendation = ref<Consultation | null | undefined>(null)
  const currentConsultationUpdateBus = useEventBus<Consultation>('consultation-updated')
  const pusher = ref<Pusher | null>(null)
  const channel = ref<Channel | null>(null)
  const error = ref<string | null>(null)
  const consultationMedium = ref<ValueOf<typeof CONSULTATION_MEDIUMS> | null>(null)
  const hasOpenConsultation = computed(() => (currentConsultation.value && currentConsultation.value.status !== consultationStatus.closed) ?? false)
  const hasAvailableConsultation = computed(() => authStore.user && authStore.isSubscribed && authStore.user.subscription.can_create_consultation)

  const updateCurrentConsultation = async (data: Consultation) => {
    if (!currentConsultation.value) {
      currentConsultation.value = data
      return
    }

    if (currentConsultation.value.status === consultationStatus.closed) {
      currentConsultation.value = null
      await updateCurrentConsultation(data)
      return
    }

    if (data?.id !== currentConsultation.value?.id) {
      console.log('updateCurrentConsultation Conflict ', data, currentConsultation.value)
      return
    }

    currentConsultation.value = {
      ...currentConsultation.value,
      ...data,
      doctor_name: data.doctor_name || currentConsultation.value.doctor_name,
      doctor_average_rating: data.doctor_average_rating || currentConsultation.value.doctor_average_rating,
      doctor_avatar: data.doctor_avatar || currentConsultation.value.doctor_avatar,
    }
  }

  const activateLastConsultation = async () => {
    try {
      const consultation = await getLastConsultation()
      if (consultation && consultation.status !== CONSULTATION_STATUSES.CLOSED) {
        await updateCurrentConsultation(consultation)
        if (!consultation.pusherAppKey || !consultation.pusherChannel) {
          throw new Error('Pusher app key not found in response')
        }

        await setupPusher(consultation.pusherAppKey, consultation.pusherChannel)
      }else if (consultation?.recommendation && !consultation?.recommendation?.seen_at && differenceInDaysFromNow(consultation.recommendation.created_at) <= 7) {
        pendingRecommendation.value = consultation
      }
    } catch (e) {
      if (isAxiosError(e)) {
        error.value = e?.response?.data?.errors
      } else if (e instanceof Error) {
        error.value = e.message
      } else {
        error.value = 'Something went wrong'
      }
      console.error(
        'Error while activating last consultation',
        e,
      )
    }
  }

  const getLastConsultation = async (input: GetConsultationForm = {}) => {
    input.sort = '-id'
    input.per_page = 1

    if (input.include) {
      input.include = ',pusherChannel,pusherAppKey,recommendation'
    } else {
      input.include = 'pusherChannel,pusherAppKey,recommendation'
    }

    const response = await useForm<Record<string, unknown>, Pagination<Consultation>>(
      'get', '/consultations/',
      input,
    ).submit()
    if (!response.data.total) {
      return null
    }
    return response.data.data[0]
  }

  const createConsultation = async (question: string, medium: string, message_id: number) => {
    const { data } = await useForm<{
      question: string,
      medium: string,
      message_id: number,
      include: string
    }, Consultation>(
      'post',
      '/consultations',
      {
        // question: 'ammar testing cons',
        question,
        medium,
        message_id,
        include: 'pusherChannel,pusherAppKey',
      }).submit()

    await updateCurrentConsultation(data)
    if (!data.pusherAppKey || !data.pusherChannel) {
      throw new Error('Pusher app key not found in response')
    }

    await setupPusher(data.pusherAppKey, data?.pusherChannel)
    return data

  }

  const getConsultation = async (consultation_id: number, input: GetConsultationForm = {}) => {
    const { data } = await useForm<GetConsultationForm, Consultation>('get', `/consultations/${consultation_id}`, input).submit()
    return data
  }
  const getConsultationSummary = async (consultation_id: number) => {
    const { data } = await useForm<Record<string, unknown>, ConsultationSummary>('get', `/consultations/${consultation_id}/summary`, {}).submit()
    return data
  }

  const cancelConsultation = async (consultation_id: number) => {
    const { data } = await useForm<Record<string, unknown>, Consultation>(
      'post',
      `/consultations/${consultation_id}/cancel`,
      {}
    ).submit()

    return data
  }
  const deleteConsultation = async (consultation_id: number) => {
    await useForm<Record<string, unknown>, Consultation>(
      'delete',
      `/consultations/${consultation_id}`,
      {}
    ).submit()
  }
  const markRecommendationSeen = async (consultation_id: number) => {
    await useForm<Record<string, unknown>, Recommendation>('post', `/consultations/${consultation_id}/recommendation-seen`, {}).submit()
    if(consultation_id == pendingRecommendation.value?.id){
      pendingRecommendation.value = null
    }
  }

  const createPusher = (appKey: string) => {
    return new Pusher(appKey, {
      cluster: 'eu',
      channelAuthorization: {
        endpoint: '/api/consultations/pusher-auth',
        transport: 'ajax',
        headers: {
          'Authorization': 'Bearer ' + authStore.getToken(),
        },
      },
    })
  }

  const setupPusher = async (appKey: string, channelName: string) => {
    cleanupPusher()
    pusher.value = createPusher(appKey)

    channel.value = pusher.value.subscribe(channelName)
    channel.value.bind_global(async (eventName: string, data: Consultation) => {
      console.log(eventName, data)
      if (!currentConsultation.value) {
        cleanupPusher()
        return
      }
      if (!['call-status', 'doctor-assigned'].includes(eventName)) {
        return
      }
      await updateCurrentConsultation(data)
      currentConsultationUpdateBus.emit(currentConsultation.value)
      if (currentConsultation.value.status === CONSULTATION_STATUSES.CLOSED) {
        cleanupPusher()
      }
    })
  }

  const cleanupPusher = () => {
    if (channel.value) {
      channel.value.unbind_global()
      pusher.value?.unsubscribe(channel.value.name)
      channel.value = null
    }
    pusher.value = null
  }

  const $reset = async () => {
    cleanupPusher()
    currentConsultation.value = null
  }

  onMounted(async () => {
    if (authStore.isAuthenticated) {
      await activateLastConsultation()
    }
  })

  return {
    // state
    consultationMedium,
    currentConsultation,
    error,
    // computed
    hasOpenConsultation,
    hasAvailableConsultation,
    // actions
    createConsultation,
    cancelConsultation,
    getConsultation,
    getConsultationSummary,
    deleteConsultation,
    $reset,
    // pusher helpers
    cleanupPusher,
    createPusher,
    getLastConsultation,
    activateLastConsultation,
    markRecommendationSeen,
    pendingRecommendation,
  }
})
